import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Shield, Target, Coins, Users, Check, ChevronDown, Filter, X } from 'lucide-react';
import { NavataraFilterType, TaraType } from '../types/muhurta';
import { NAKSHATRAS } from '../utils/vedicEngine';

interface NavataraFilterDropdownProps {
  selectedFilter: NavataraFilterType;
  customTaras: TaraType[];
  onChange: (filter: NavataraFilterType, customTaras?: TaraType[]) => void;
  janmaNakIdx?: number;
  partnerCount?: number;
  lang?: 'en' | 'hi';
  compact?: boolean;
}

export const NavataraFilterDropdown: React.FC<NavataraFilterDropdownProps> = ({
  selectedFilter,
  customTaras,
  onChange,
  janmaNakIdx = 0,
  partnerCount = 1,
  lang = 'en',
  compact = false
}) => {
  const isHi = lang === 'hi';
  const [isOpen, setIsOpen] = useState(false);
  const [draftFilter, setDraftFilter] = useState<NavataraFilterType>(selectedFilter);
  const [draftCustomTaras, setDraftCustomTaras] = useState<TaraType[]>(customTaras);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync draft with prop when popover is closed
  useEffect(() => {
    if (!isOpen) {
      setDraftFilter(selectedFilter);
      setDraftCustomTaras(customTaras);
    }
  }, [selectedFilter, customTaras, isOpen]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Helper to get matching 3 Nakshatras for a Tara given Janma Nakshatra index (0-26)
  const getMatchingStarNames = (offsets: number[]): string[] => {
    return offsets.map(off => NAKSHATRAS[(janmaNakIdx + off) % 27]?.name || '');
  };

  const kshemaStars = getMatchingStarNames([3, 12, 21]); // 4th, 13th, 22nd star
  const sadhakaStars = getMatchingStarNames([5, 14, 23]); // 6th, 15th, 24th star
  const sampatStars = getMatchingStarNames([1, 10, 19]); // 2nd, 11th, 20th star
  const mitraStars = getMatchingStarNames([7, 8, 16, 17, 25, 26]); // 8th, 9th, 17th, 18th, 26th, 27th star

  const filterOptions: {
    id: NavataraFilterType;
    label: string;
    labelHi: string;
    subtitle: string;
    subtitleHi: string;
    icon: any;
    color: string;
    matchingStars: string[];
    cycleInfo: string;
  }[] = [
    {
      id: 'all_auspicious',
      label: 'All Auspicious Taras',
      labelHi: 'समस्त शुभ नवतारे (सम्पत्, क्षेम, साधक, मित्र)',
      subtitle: 'Permits any of the 5 beneficial Vedic Taras',
      subtitleHi: 'सभी ५ शुभ ताराओं (सम्पत्, क्षेम, साधक, मित्र, परम मित्र) की अनुमति',
      icon: Sparkles,
      color: 'text-amber-600 bg-amber-50 border-amber-200',
      matchingStars: [],
      cycleInfo: isHi ? 'पूर्ण वैदिक लचीलापन' : 'Broadest auspicious coverage'
    },
    {
      id: 'kshema_only',
      label: 'Kshema Only (4th, 13th, 22nd)',
      labelHi: 'केवल क्षेम तारा (४था, १३वां, २२वां तारा)',
      subtitle: 'Health, Bedrock Protection, Security & Peace',
      subtitleHi: 'आरोग्य, सुरक्षा, अचल स्थिरता, भय-निवारण व दीर्घकालिक शांति',
      icon: Shield,
      color: 'text-emerald-700 bg-emerald-50 border-emerald-300',
      matchingStars: kshemaStars,
      cycleInfo: isHi ? 'देह क्षेम (४) • कर्म क्षेम (१३) • मैत्र क्षेम (२२)' : 'Personal (4) • Enterprise (13) • Sovereign (22)'
    },
    {
      id: 'sadhaka_only',
      label: 'Sadhaka Only (6th, 15th, 24th)',
      labelHi: 'केवल साधक तारा (६ठा, १५वां, २४वां तारा)',
      subtitle: 'Goal Accomplishment, High Achievement & Mastery',
      subtitleHi: 'कार्य सिद्धि, संकल्प पूर्ति, लक्ष्य भेदन, प्रतियोगी विजय व सिद्धि',
      icon: Target,
      color: 'text-blue-700 bg-blue-50 border-blue-300',
      matchingStars: sadhakaStars,
      cycleInfo: isHi ? 'कार्य साधक (६) • व्यापार साधक (१५) • पूर्ण साधक (२४)' : 'Execution (6) • Commercial (15) • Mastery (24)'
    },
    {
      id: 'sampat_only',
      label: 'Sampat Only (2nd, 11th, 20th)',
      labelHi: 'केवल सम्पत् तारा (२रा, ११वां, २०वां तारा)',
      subtitle: 'Financial Abundance, Commercial Gain & Revenue',
      subtitleHi: 'धन संचय, राजस्व वृद्धि, वित्तीय लाभ व समृद्धि',
      icon: Coins,
      color: 'text-amber-700 bg-amber-50 border-amber-300',
      matchingStars: sampatStars,
      cycleInfo: isHi ? 'सम्पत् (२) • कोष सम्पत् (११) • साम्राज्य सम्पत् (२०)' : 'Liquidity (2) • Treasury (11) • Asset (20)'
    },
    {
      id: 'mitra_only',
      label: 'Mitra & Param Mitra (8th & 9th)',
      labelHi: 'मित्र एवं परम मित्र तारा (८वां व ९वां तारा)',
      subtitle: 'Alliances, Joint Ventures, Goodwill & Harmony',
      subtitleHi: 'साझेदारी, सौहार्द, संविदा, संबंध व परम मैत्री',
      icon: Users,
      color: 'text-purple-700 bg-purple-50 border-purple-300',
      matchingStars: mitraStars.slice(0, 4),
      cycleInfo: isHi ? 'मित्र (८) व परम मित्र (९) की समस्त ३ आवृत्तियां' : 'All 3 cycles of Mitra & Param Mitra'
    }
  ];

  const handleApply = () => {
    onChange(draftFilter, draftFilter === 'custom' ? draftCustomTaras : undefined);
    setIsOpen(false);
  };

  const handleResetToAll = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('all_auspicious');
    setDraftFilter('all_auspicious');
    setIsOpen(false);
  };

  const activeOption = filterOptions.find(f => f.id === selectedFilter) || filterOptions[0];
  const isFiltered = selectedFilter !== 'all_auspicious';

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        id="navatara-filter-btn"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-controls="navatara-filter-popover"
        aria-label={isHi ? 'विशिष्ट नवतारा फ़िल्टर' : 'Filter by specific Navatara'}
        onClick={() => setIsOpen(!isOpen)}
        className={`inline-flex items-center gap-1.5 rounded-xl font-bold transition-all cursor-pointer shadow-2xs border ${
          compact ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-xs'
        } ${
          isFiltered 
            ? 'bg-amber-50 text-amber-900 border-amber-300 ring-2 ring-amber-400/30' 
            : 'bg-astro-card hover:bg-astro-card/80 text-astro-primary border-astro-border'
        }`}
        title={isHi ? 'विशिष्ट नवतारा प्रकार चुनें' : 'Filter by specific Navatara (Kshema, Sadhaka, Sampat)'}
      >
        <activeOption.icon className={`w-3.5 h-3.5 ${isFiltered ? 'text-amber-700' : 'text-astro-accent'}`} />
        <span className="truncate max-w-[130px] sm:max-w-[180px]">
          {isFiltered
            ? (selectedFilter === 'kshema_only' ? (isHi ? 'क्षेम केवल' : 'Kshema Only')
              : selectedFilter === 'sadhaka_only' ? (isHi ? 'साधक केवल' : 'Sadhaka Only')
              : selectedFilter === 'sampat_only' ? (isHi ? 'सम्पत् केवल' : 'Sampat Only')
              : selectedFilter === 'mitra_only' ? (isHi ? 'मित्र केवल' : 'Mitra Only')
              : (isHi ? 'कस्टम तारा' : 'Custom Tara'))
            : (isHi ? 'नवतारा: सभी शुभ' : 'Tara: All Auspicious')}
        </span>

        {isFiltered && (
          <span
            role="button"
            tabIndex={0}
            onClick={handleResetToAll}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { handleResetToAll(e as any); } }}
            aria-label={isHi ? 'सभी शुभ ताराओं पर वापस जाएं' : 'Reset to all auspicious'}
            className="p-0.5 rounded-full hover:bg-amber-200 text-amber-900 transition-colors ml-0.5"
            title={isHi ? 'सभी शुभ ताराओं पर वापस जाएं' : 'Reset to all auspicious'}
          >
            <X className="w-3 h-3" />
          </span>
        )}

        <ChevronDown className={`w-3 h-3 text-astro-secondary-text transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Popover */}
      {isOpen && (
        <div
          id="navatara-filter-popover"
          role="dialog"
          aria-labelledby="navatara-filter-btn"
          className="absolute right-0 sm:left-0 z-50 mt-2 w-80 sm:w-96 rounded-2xl bg-white border border-astro-border shadow-xl p-4 space-y-3.5 animate-in fade-in slide-in-from-top-2"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-astro-border/60 pb-2.5">
            <div className="flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-astro-accent" />
              <h4 className="text-xs font-bold text-astro-primary font-serif">
                {isHi ? 'नवतारा चक्र चयन' : 'Navatara Chakra Filtering'}
              </h4>
            </div>
            {partnerCount > 1 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200">
                👥 {partnerCount} {isHi ? 'साझेदार सुरक्षित' : 'Partners Protected'}
              </span>
            )}
          </div>

          <p className="text-[11px] text-astro-secondary-text leading-tight">
            {isHi
              ? 'अपनी जन्म कुंडली के चंद्रमा के सापेक्ष अपनी मनोवांछित ऊर्जा का चयन करें। यह फ़िल्टर कॉम्बो एवं नवतारा दोनों व्यू में सक्रिय रहेगा।'
              : 'Select your targeted energy archetype from your birth Moon. Persists across both Combo and Navtara views.'}
          </p>

          {/* Options List */}
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {filterOptions.map(opt => {
              const isSelected = draftFilter === opt.id;
              const Icon = opt.icon;

              return (
                <div
                  key={opt.id}
                  onClick={() => {
                    setDraftFilter(opt.id);
                    onChange(opt.id, draftCustomTaras);
                  }}
                  className={`p-2.5 rounded-xl border text-left cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-astro-primary/5 border-astro-primary ring-1 ring-astro-primary/40 shadow-2xs'
                      : 'bg-astro-card/30 hover:bg-astro-card/80 border-astro-border/60'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                      <div className={`p-1.5 rounded-lg border shrink-0 mt-0.5 ${opt.color}`}>
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-bold text-astro-primary">
                            {isHi ? opt.labelHi : opt.label}
                          </span>
                        </div>
                        <p className="text-[10px] text-astro-secondary-text leading-tight mt-0.5">
                          {isHi ? opt.subtitleHi : opt.subtitle}
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <div className="w-4 h-4 rounded-full bg-astro-primary text-white flex items-center justify-center shrink-0 mt-1">
                        <Check className="w-2.5 h-2.5" />
                      </div>
                    )}
                  </div>

                  {/* Matching Stars for native */}
                  {opt.matchingStars.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-astro-border/40 flex flex-wrap items-center gap-1">
                      <span className="text-[9px] font-bold text-astro-secondary-text uppercase tracking-wider">
                        {isHi ? 'आपके जन्म नक्षत्र के अनुकूल तारे:' : 'Your Native Stars:'}
                      </span>
                      {opt.matchingStars.map((st, sIdx) => (
                        <span
                          key={sIdx}
                          className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-white text-astro-primary border border-astro-border shadow-2xs"
                        >
                          {st}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Cycle info */}
                  <div className="text-[9px] text-astro-secondary-text/70 italic mt-1">
                    {opt.cycleInfo}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer Actions */}
          <div className="pt-2.5 border-t border-astro-border/60 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                setDraftFilter('all_auspicious');
                onChange('all_auspicious');
                setIsOpen(false);
              }}
              className="text-[11px] font-semibold text-astro-secondary-text hover:text-astro-primary hover:underline cursor-pointer"
            >
              {isHi ? 'रीसेट (सभी शुभ)' : 'Reset to All'}
            </button>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-lg border border-astro-border text-astro-secondary-text hover:bg-astro-card cursor-pointer"
              >
                {isHi ? 'रद्द करें' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="text-[11px] font-bold px-3.5 py-1 bg-astro-primary text-white rounded-lg hover:bg-astro-secondary shadow-2xs active:scale-95 cursor-pointer flex items-center gap-1"
              >
                <Check className="w-3 h-3" />
                <span>{isHi ? 'फ़िल्टर लागू करें' : 'Apply Filter'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
